/* eslint-disable eqeqeq */
import React, { useMemo, useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useFind, useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import moment from 'moment';
import { formatMillisToDateString, utcDateToMillis } from 'utils/dateManagement';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { sortObjectArray } from 'utils/arrayHelpers';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { DateWrapper } from 'components/formInputs/wrappers/dateWrapper';
import { TextboxTypeWrapper } from 'components/formInputs/wrappers/textboxTypeWrapper';
import { Loader } from 'components/indicators/loader';
import { FormButton } from 'components/buttons/formButton';
import { PricingDataBlock } from './components/dataBlock';
import { IikoDiscountUsages } from './components/iikoUsages';
import { RKeeperDiscountUsages } from './components/rkeeperUsages';

const types = { FIXED: 'Fixed', PERCENTAGE: 'Percent' };
const discountTypes = { COUPON: 'Coupon', PROMOTION: 'Static' };

export const PricingAddEditForm = ({ itemData, isEdit }) => {
  const navigate = useNavigate();
  const { state: companyState, addNew, editById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const [selectedType, setSelectedType] = useState(types.PERCENTAGE);
  const [selectedIntegration, setSelectedIntegration] = useState(itemData?.integration_id);
  const [externalDiscountSelected, setExternalDiscountSelected] = useState(false);

  const [selectedClassifications, setSelectedClassifications] = useState(
    itemData?.classifications || [],
  );

  const [selectedSections, setSelectedSections] = useState(
    itemData?.sections?.split('~') || [],
  );

  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);

  const [usages, setUsages] = useState(itemData?.usages || []);
  const [dayTimes, setDayTimes] = useState(itemData?.day_times || []);
  const [times, setTimes] = useState(itemData?.times || []);

  const [isSaving, setIsSaving] = useState(false);

  const [, classifications] = useFind({
    collectionName: Config.DATA.COLLECTIONS.CLASSIFICATIONS,
    secretKey: auth.token,
    pageSize: 100,
    pageNumber: 0,
    projection: { _id: 1, name: 1, parent_name: 1, external_id: 1, external_parent_id: 1 },
    sort: { name: 1 },
    filter: `{ company: ObjectId("${auth.company?.company}") }`,
  });

  const [, tableSections] = useAggregate({
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_TABLE_SECTIONS,
    secretKey: auth.token,
    filter: { company: auth.company?.company },
  });

  const [, integrations, integrationsLoading] = useAggregate({
    collectionName: Config.DATA.COLLECTIONS.COMPANY,
    aggregateId: '0f6cc82e-096b-4882-bd1e-0ee4b46bdd57',
    secretKey: auth.token,
    filter: { company: auth.company?.company },
  });

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const parseDateString = (current, original) => {
    if (original === '') {
      return null;
    }

    if (moment(original, 'YYYY-MM-DD', true).isValid()) {
      return new Date(original);
    }

    return current;
  };

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    isEditForm: yup.boolean().default(isEdit),
    enabled: yup.boolean().default(itemData?.enabled || false),
    integration_id: yup.string().default(itemData?.integration_id),
    name: yup.string().required('Please enter a discount name.').default(itemData?.name),
    sections: yup.string().nullable().default(itemData?.sections),
    quantity_mode: yup.string().default(itemData?.quantity_mode),
    with_others: yup.boolean().default(itemData?.with_others || false),
    min_quantity: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_quantity),
    min_amount: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_amount),
    min_order_value: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_order_value),
    inner_priority: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.inner_priority),
    discountValue: yup
      .number()
      .min(0, 'Discount must be greater than 0')
      .typeError('Please enter a valid number.')
      .nullable(false)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : -1))
      .required('Please enter a discount value.')
      .default(itemData?.percent || itemData?.fixed),
    from: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseDateString)
      .nullable(true)
      .default(itemData?.from ? formatMillisToDateString(itemData?.from) : ''),

    until: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseDateString)
      .min(yup.ref('from'), 'End date should be later than start date')
      .nullable(true)
      .default(itemData?.until ? formatMillisToDateString(itemData?.until) : ''),
    coupon: yup.string().default(itemData?.coupon),
    classifications: yup.array().nullable().default(itemData?.classifications || []),
  });

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData) {
      setSelectedType(itemData?.fixed ? types.FIXED : types.PERCENTAGE);

      setSelectedIntegration(itemData?.integration_id);

      reset(itemData);
      setValue('discountValue', itemData.mode === types.FIXED ? itemData.fixed : itemData.percent);
      setValue('from', formatMillisToDateString(itemData.from));
      setValue('until', formatMillisToDateString(itemData.until));
    }
  }, [itemData]);

  const integrationsOptions = useMemo(() => {
    if (!integrations || integrations.length === 0) {
      return [{ label: '-', value: '-1' }];
    }

    const data = integrations[0];
    const options = [];

    const formatName = (integ, availableRestaurants) => {
      if (integ.name) {
        return integ.name;
      }

      const restaurantNames = availableRestaurants.map((r) => r.name);

      return restaurantNames.join(', ');
    };

    const filterAvailableRestaurants = (integ) => {
      if (!integ.restaurants || integ.restaurants.length === 0) {
        return integ.restaurants;
      }

      const filteredRestaurants = integ.restaurants?.filter((x) => !!x._id);

      return filteredRestaurants;
    };

    (data.iiko || []).forEach((integration) => {
      const availableRestaurants = filterAvailableRestaurants(integration);

      if (!availableRestaurants || availableRestaurants.length === 0) {
        return;
      }

      options.push({ value: integration._id, label: formatName(integration, availableRestaurants), integration: 'iiko', restaurants: availableRestaurants });
    });

    (data.rkeeper || []).forEach((integration) => {
      const availableRestaurants = filterAvailableRestaurants(integration);

      if (!availableRestaurants || availableRestaurants.length === 0) {
        return;
      }

      options.push({ value: integration._id, label: formatName(integration, availableRestaurants), integration: 'rkeeper', restaurants: availableRestaurants });
    });

    sortObjectArray(options, 'label', 1);

    options.unshift({ label: '-', value: '' });

    return options;
  }, [integrations]);

  const integrationProvider = selectedIntegration ? integrationsOptions?.find((x) => selectedIntegration === x.value)?.integration : null;

  const getProductClassificationsOptions = () => {
    const filteredClassifications = classifications?.filter((c) => !selectedClassifications.includes(c._id));
    const options = filteredClassifications?.map((x) => ({
      label: x.parent_name ? `${x.parent_name} - ${x.name}` : x.name,
      value: x._id,
    }));

    sortObjectArray(options, 'label', 1);

    options?.unshift({
      label: selectedClassifications && selectedClassifications.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const getRestaurantsDataSource = () => {
    const filteredRestaurants = companyState.restaurants?.filter((c) => !selectedRestaurants.includes(c._id));
    const options = filteredRestaurants?.map((x) => ({
      label: x.name,
      value: x._id,
    }));

    options?.unshift({
      label: selectedRestaurants && selectedRestaurants.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const getSectionsOptions = () => {
    const filteredSections = tableSections?.filter((x) => !selectedSections.includes(x.name));
    const options = filteredSections?.map((x) => ({
      label: x.name,
      value: x.name,
    }));

    sortObjectArray(options, 'label', 1);

    options?.unshift({
      label: selectedSections && selectedSections.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const renderSelectedClassifications = () => {
    if (!selectedClassifications) return null;

    return (
      <ul className="mt-1">
        {selectedClassifications.map((selected) => {
          const classification = classifications?.find(
            (x) => x._id === selected,
          );

          if (!classification) return null;
          return (
            <div key={classification._id}>
              <SelectedItemButton
                name={classification?.name}
                onClick={externalDiscountSelected ? null : () => {
                  const newClassifications = selectedClassifications.filter((x) => x !== classification?._id);

                  setSelectedClassifications(newClassifications);
                  setValue('classifications', newClassifications);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <ul className="mt-1">
        {selectedRestaurants.map((selected) => {
          const restaurant = companyState.restaurants?.find(
            (x) => x._id === selected,
          );

          if (!restaurant) return null;
          return (
            <div key={restaurant._id}>
              <SelectedItemButton
                name={restaurant?.name}
                onClick={() => {
                  const newRestaurants = selectedRestaurants.filter((x) => x !== restaurant?._id);

                  setSelectedRestaurants(newRestaurants);
                  setValue('restaurants', newRestaurants);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  const renderSelectedSections = () => {
    if (!selectedSections) return null;

    return (
      <ul className="mt-1">
        {selectedSections.map((selected) => {
          return (
            <div key={selected}>
              <SelectedItemButton
                name={selected}
                onClick={() => {
                  const newSections = selectedSections.filter((x) => x !== selected);

                  setSelectedSections(newSections);
                  setValue('sections', newSections && newSections.length > 0 ? newSections.join('~') : '');
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  const onSubmit = async (data) => {
    setIsSaving(true);

    const submitData = {
      company: auth.company?.company,
      type: data.coupon ? discountTypes.COUPON : discountTypes.PROMOTION,
      enabled: data.enabled,
      name: data.name,
      // coupon: data.coupon,
      with_others: data.with_others,
      inner_priority: data.inner_priority || 0,
      min_amount: data.min_amount || 0,
      sections: data.sections,
      classifications: data.classifications,
      restaurants: data.restaurants,
      mode: selectedType,
      percent: selectedType === types.PERCENTAGE ? data.discountValue : 0,
      fixed: selectedType === types.FIXED ? data.discountValue : 0,
      from: data.from ? utcDateToMillis(data.from) : null,
      until: data.until ? utcDateToMillis(data.until) : null,
      integration_id: selectedIntegration,
      usages: (usages || []).map((x) => ({
        priority: x.priority,
        type: x.type,
        usage_id: x.usage_id,
        restaurants: (x.restaurants || []).map((y) => y.value),
      })),
      day_times: dayTimes || [],
      times: times || [],
    };

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.DISCOUNTS,
          data: submitData,
          token: auth.token,
        },
        () => {
          navigate('/discounts');
        },
        () => {
          setIsSaving(false);
        },
      );
    } else {
      delete submitData.company;
      delete submitData.integration_id;

      if (integrationProvider === 'rkeeper') {
        delete submitData.usages;
        delete submitData.day_times;
        delete submitData.times;
      } else {
        // restaurants to oid?
      }

      submitData.classifications =
        submitData.classifications && submitData.classifications.length > 0
          ? submitData.classifications.map((x) => ({ $oid: x }))
          : [];

      submitData.restaurants =
        submitData.restaurants && submitData.restaurants.length > 0
          ? submitData.restaurants.map((x) => ({ $oid: x }))
          : [];

      const stringifiedData = JSON.stringify({ ...submitData });

      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.DISCOUNTS,
          id: data._id,
          updateClause: `{ $set: ${stringifiedData} }`,
          token: auth.token,
        },
        () => {
          navigate('/discounts');
        },
        () => {
          setIsSaving(false);
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  if (integrationsLoading > 0) {
    return <Loader />;
  }

  return (
    <>
      <PageTitle
        title="Discount"
        breadcrumbs={[{ path: '/discounts', title: 'Discounts' }]}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="space-y-6">

              <PricingDataBlock title="Integration" subtitle="Select your used integration and/or restaurants">
                {/* <TextboxWrapper
                  register={register}
                  name="integrationName"
                  title="Integration"
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  disabled
                /> */}
                <DropdownWrapper
                  register={register}
                  name="integration_id"
                  title="Integration"
                  options={integrationsOptions}
                  error={errors?.integration}
                  responsiveClassName="col-span-6 lg:col-span-4"
                  onChange={(e) => {
                    setSelectedIntegration(e.target.value);
                  }}
                  disabled={itemData?._id}
                />

                {!!itemData?.external_id && (
                <TextboxWrapper
                  register={register}
                  name="external_id"
                  title="External ID"
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  disabled
                />
                )}

                {!!itemData?.external_sub_id && (
                <TextboxWrapper
                  register={register}
                  name="external_sub_id"
                  title="External detail ID"
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  disabled
                />
                )}

                <DropdownWrapper
                  name="restaurants"
                  title="Restaurants"
                  options={getRestaurantsDataSource()}
                  error={errors?.restaurants}
                  responsiveClassName="col-span-6 lg:col-span-4"
                  onChange={(e) => {
                    setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                    setValue('restaurants', [...selectedRestaurants, e.target.value]);
                  }}
                  renderSelections={renderSelectedRestaurants}
                />

                {integrationProvider === 'rkeeper' && !isEdit && (
                <div className="col-span-6 text-sm">
                  <span className="text-orange-400">Discounts for this integration need to by synced and cannot be added from here.</span>
                </div>
                )}

              </PricingDataBlock>

              {(integrationProvider !== 'rkeeper' || isEdit) && (
              <>
                <PricingDataBlock title="Discount" subtitle="Set your discount identifiers">
                  <CheckboxWrapper
                    register={register}
                    name="enabled"
                    title="Enabled"
                    subtitle="only enabled discount will be applicable"
                    responsiveClassName="col-span-6 lg:col-span-4"
                  />

                  <TextboxWrapper
                    register={register}
                    name="name"
                    title="Name*"
                    error={errors?.name}
                    responsiveClassName="col-span-6 lg:col-span-4"
                  />

                  {/* <TextboxWrapper
                    register={register}
                    name="coupon"
                    title="Coupon code"
                    error={errors?.coupon}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  /> */}
                </PricingDataBlock>

                <PricingDataBlock title="Amount" subtitle="Select the size of the discount">
                  <TextboxTypeWrapper
                    register={register}
                    title="Amount*"
                    name="discountValue"
                    error={errors?.discountValue}
                    responsiveClassName="col-span-6 md:col-span-6 lg:col-span-4"
                    options={[
                      { label: 'Percentage', value: types.PERCENTAGE },
                      { label: 'Fixed', value: types.FIXED },
                    ]}
                    onChangeSelectedType={setSelectedType}
                    selectedType={selectedType}
                  />
                </PricingDataBlock>

                <PricingDataBlock title="Restrictions" subtitle="Select discount restrictions">
                  <DateWrapper
                    register={register}
                    name="from"
                    title="Date from"
                    error={errors?.from}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                  />

                  <DateWrapper
                    register={register}
                    name="until"
                    title="Date to"
                    error={errors?.until}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                  />

                  <CheckboxWrapper
                    register={register}
                    name="with_others"
                    title="Combine with others"
                    subtitle="will always be applied"
                    responsiveClassName="col-span-6 lg:col-span-4"
                  />

                  <TextboxWrapper
                    register={register}
                    name="inner_priority"
                    title={integrationProvider === 'rkeeper' ? 'Detail priority' : 'Priority'}
                    subtitle={integrationProvider === 'rkeeper' ? 'Order of discount detail inside discount' : null}
                    error={errors?.inner_priority}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  />

                  <TextboxWrapper
                    register={register}
                    name="min_amount"
                    title={integrationProvider === 'rkeeper' ? 'Detail min. amount' : 'Minimum order amount'}
                    error={errors?.inner_priority}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                  />

                  <DropdownWrapper
                    name="classifications"
                    title="Classifications"
                    options={getProductClassificationsOptions()}
                    error={errors?.classifications}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                    onChange={(e) => {
                      setSelectedClassifications((oldArray) => [...oldArray, e.target.value]);
                      setValue('classifications', [...selectedClassifications, e.target.value]);
                    }}
                    renderSelections={renderSelectedClassifications}
                  />

                  <DropdownWrapper
                    name="sections"
                    title="Table sections"
                    options={getSectionsOptions()}
                    error={errors?.sections}
                    responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                    onChange={(e) => {
                      const newArray = [...(selectedSections || []), e.target.value];

                      setSelectedSections(newArray);
                      setValue('sections', newArray.join('~'));
                    }}
                    renderSelections={renderSelectedSections}
                  />

                </PricingDataBlock>

                <PricingDataBlock
                  title="Usages"
                  subtitle="More specific usage of discount"
                >
                  <div className="col-span-6">
                    {(!integrationProvider || integrationProvider === 'iiko') && (
                    <IikoDiscountUsages
                      dayTimeUsages={dayTimes}
                      onSave={(val) => {
                        setDayTimes((prev) => {
                          const foundInd = prev ? prev.findIndex((x) => x.usage_id === val.usage_id) : -1;

                          if (foundInd >= 0) {
                            const newArray = [...prev];

                            newArray[foundInd] = val;
                            return newArray;
                          }

                          return [...prev, val];
                        });
                      }}
                      onDelete={(val) => {
                        setDayTimes((prev) => {
                          const newArray = [...(prev || [])].filter(((x) => x.usage_id !== val.usage_id));

                          return newArray;
                        });
                      }}
                    />
                    )}
                    {integrationProvider === 'rkeeper' && (
                      <RKeeperDiscountUsages usages={usages} dayTimes={dayTimes} times={times} restaurants={companyState.restaurants} />
                    )}
                  </div>
                </PricingDataBlock>
              </>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate('/discounts')}
                >
                  Cancel
                </button>
                {(integrationProvider !== 'rkeeper' || isEdit) && <FormButton text="Save" isLoading={isSaving} />}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
